import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { consoleApi } from "../services/consoleApiService";
import { useAuth } from "./AuthContext";
import { MASTER_EMAIL } from "../constants/api";

const ConsoleAuthContext = createContext(null);
const CONSOLE_SESSION_RETRY_DELAY_MS = 400;

export function useConsoleAuth() {
  const ctx = useContext(ConsoleAuthContext);
  if (!ctx) {
    throw new Error("useConsoleAuth must be used within ConsoleAuthProvider");
  }
  return ctx;
}

/** Check if user is the master admin (by email). Exported for AppWrapper and ConsoleLayout. */
export function isMasterUser(user) {
  if (!user?.email?.trim?.() || !MASTER_EMAIL?.trim?.()) return false;
  return user.email.trim().toLowerCase() === MASTER_EMAIL.trim().toLowerCase();
}

export function ConsoleAuthProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const initialCheckDone = useRef(false);

  const checkSession = useCallback(async (isRetry = false) => {
    if (!isRetry) setLoading(true);
    try {
      await consoleApi.auth.me();
      setIsAdmin(true);
    } catch (err) {
      const is401 = err?.response?.status === 401;
      if (is401 && !isRetry && !initialCheckDone.current) {
        initialCheckDone.current = true;
        await new Promise((r) => setTimeout(r, CONSOLE_SESSION_RETRY_DELAY_MS));
        await checkSession(true);
        return;
      }
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only call admin/me when auth has loaded and current user could be master (avoids 401s when not logged in).
  // Skip admin/me for master user: guard allows access by email, so we avoid 401s when cookies are not sent cross-origin.
  useEffect(() => {
    initialCheckDone.current = false;
    if (authLoading) return;
    if (!authUser || !isMasterUser(authUser)) {
      setLoading(false);
      setIsAdmin(false);
      return;
    }
    setLoading(false);
    setIsAdmin(false);
  }, [authLoading, authUser]);

  const adminLogout = useCallback(() => {
    setIsAdmin(false);
  }, []);

  const value = useMemo(
    () => ({
      isAdmin,
      loading,
      adminLogout,
      checkSession,
    }),
    [isAdmin, loading, adminLogout, checkSession]
  );

  return (
    <ConsoleAuthContext.Provider value={value}>
      {children}
    </ConsoleAuthContext.Provider>
  );
}
